// src/components/BlackParticleBackground.jsx
import React, { useRef, useEffect, useCallback } from "react";

const BlackParticleBackground = () => {
  const canvasRef = useRef(null);
  const animationFrameRef = useRef();
  const particlesRef = useRef([]);
  const lastTimestampRef = useRef(0);

  // 粒子类
  class Particle {
    constructor(width, height) {
      this.canvasWidth = width;
      this.canvasHeight = height;
      this.reset();
      this.size = Math.random() * 2 + 1;
    }

    reset() {
      // 随机水平位置，垂直位置在底部附近
      this.x = Math.random() * this.canvasWidth;
      this.y = this.canvasHeight + Math.random() * 100;
      // 随机上升速度
      this.speed = Math.random() * 1 + 0.5;
      // 初始透明度
      this.alpha = Math.random() * 0.5 + 0.3;
    }

    update(deltaTime) {
      // 向上移动
      this.y -= this.speed * deltaTime * 60;

      // 当粒子到达屏幕上方1/4时开始渐隐
      const fadeStartPosition = this.canvasHeight * 0.25;
      if (this.y < fadeStartPosition) {
        this.alpha = Math.max(0, (this.y / fadeStartPosition) * this.alpha);
      }

      // 当粒子移出屏幕顶部或完全透明时，重置到底部
      if (this.y < -20 || this.alpha <= 0.01) {
        this.reset();
      }
    }

    draw(ctx) {
      ctx.globalAlpha = this.alpha;
      ctx.fillStyle = "rgba(255, 255, 255, 0.8)";
      ctx.beginPath();
      ctx.arc(this.x, this.y, this.size, 0, Math.PI * 2);
      ctx.fill();
    }
  }

  // 动画循环
  const animate = useCallback((timestamp) => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext("2d");

    // 计算时间差，限制最大帧时间以保证流畅性
    const deltaTime = lastTimestampRef.current
      ? Math.min(0.05, (timestamp - lastTimestampRef.current) / 1000)
      : 0.016;
    lastTimestampRef.current = timestamp;

    // 清除画布
    ctx.clearRect(0, 0, canvas.width, canvas.height);

    // 绘制纯黑色背景
    ctx.fillStyle = "#000000";
    ctx.fillRect(0, 0, canvas.width, canvas.height);

    // 更新和绘制所有粒子
    particlesRef.current.forEach((particle) => {
      particle.update(deltaTime);
      particle.draw(ctx);
    });

    // 重置全局透明度
    ctx.globalAlpha = 1;

    animationFrameRef.current = requestAnimationFrame(animate);
  }, []);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    // 设置画布大小以匹配窗口
    const resizeCanvas = () => {
      canvas.width = window.innerWidth;
      canvas.height = window.innerHeight;
    };

    resizeCanvas();
    window.addEventListener("resize", resizeCanvas);

    // 创建50个粒子
    const particles = Array.from(
      { length: 50 },
      () => new Particle(canvas.width, canvas.height)
    );
    particlesRef.current = particles;

    // 开始动画循环
    animationFrameRef.current = requestAnimationFrame(animate);

    // 清理函数
    return () => {
      window.removeEventListener("resize", resizeCanvas);
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
      }
    };
  }, [animate]);

  return (
    <canvas
      ref={canvasRef}
      style={{
        position: "fixed",
        top: 0,
        left: 0,
        width: "100%",
        height: "100%",
        zIndex: -1,
      }}
    />
  );
};

export default BlackParticleBackground;
